import { P2GBox } from '@parcel2go/core-ui';
import { P2GText } from '@parcel2go/core-ui';
import { Suspense, useContext, useEffect, useMemo, useState } from 'react';
import {
  LinkApiModel,
  PaymentOptionApiModel,
  PaymentOptionOptionApiModel,
  PaymentOptionType,
  ShipResponse,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SmartSendItemsCache } from '../../../../lib/cache/SmartSendItemsCache';
import { SmartSendContext } from '../../../../lib/context/SmartSendContext';
import {
  MarkAsPaidItemsTempAction,
  MarkAsPaidItemsAction,
  MarkAsStagedItemsAction,
  MarkDispatchedItemAction
} from '../../../../lib/context/SmartSendContextModels';
import { SumNumber, onlyUnique } from '../../../../lib/helpers/ArrayHelper';
import { ProviderGroup, ProviderGroupState } from '../../../../lib/helpers/SmartSendBulkActionApplier';
import { GetBookableBasedOnIds } from '../../../../lib/helpers/GetBookableBasedOnIds';
import { GetBookableBasedOnSelection } from '../../../../lib/helpers/GetBookableBasedOnSelection';
import { GetItemsByProvider } from '../../../../lib/helpers/GetItemsByProvider';
import Loading from '../../../../../../components/loading/Loading';
import BookAll from './BookAll';
import ProviderGroups from './ProviderGroups';
import { ToInlineCommaList } from '../../../../lib/helpers/StringHelper';
import { useEventEmitter } from '../../../../lib/em/Hooks';
import { BookEventType, Consts } from '../../../../lib/em/EmitTypes';
import Confirm, { confirmItem, confirmType } from './Confirm';
import LabelSummary from './LabelSummary';
import { DownloadContext } from '../../../../lib/context/DownloadContext';
import { uid } from '../../../../lib/helpers/uid';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { ExtendedOrderCache } from '../../../../lib/cache/ExtendedOrderCache';
import { EventSections } from '../../../../lib/events/EventLogger';
import { ClickType } from '../../../../lib/events/ClickType';
import { Tagged } from '../../../Tagged';
import { CanUseDiscount } from './CanUseDiscount';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import useRefreshCollectionDates from './RefreshCollectionDates';
import { P2GSidePanel } from '~/components/sidepanel';
import { useRecordPurchaseAnalytics } from './useRecordPurchaseAnalytics';
import { SmartSendProvider } from '~/lib/consts/providers';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import InstaSetting from '~/apps/user-settings/insta-setting';
import { GenerateBaseAnalyticsPayload } from '~/lib/utils/analytics';
import { AppSurface } from '~/components/surface/app-surface';
import ShopCollectionWarning from './ShopCollectionWarning';
import {
  getDispatchBlockedOrders,
  getSharedDispatchBlockedReason,
  isDeliveryToShopService
} from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import DispatchBlockedWarning from './DispatchBlockedWarning';
import { BusinessDelivery, getBusinessDeliveries } from '~/apps/smartsend/lib/helpers/BusinessDeliveryHelper';
import BusinessDeliveryNotice from './BusinessDeliveryNotice';
import useSmartSendAnalytics from '~/hooks/use-smartsend-analytics';
import KycStatusCheck from './KycStatus';

export type BookingStatusType = 'BOOKING' | 'BOOKED' | 'IDLE' | 'ERROR';

interface CachedPaymentOption {
  accountId: string;
  provider: string;
  options: PaymentOptionApiModel[];
}

interface props {
  isXs: boolean;
  filterKey: string;
}

export default function Book(props: props) {
  const { getAccountPaymentOptions, getItemsByIds, getShipmentActions, ship, verifyShip } = useSmartSendApi();
  const { RefreshCollectionDates } = useRefreshCollectionDates();
  const { record } = useRecordPurchaseAnalytics();
  const ctx = useContext(SmartSendContext);
  const dctx = useContext(DownloadContext);
  const [loading, setLoading] = useState(true);
  const [loadingManifests] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState<CachedPaymentOption[]>([]);
  const [providerGroups, setProviderGroups] = useState<ProviderGroup[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const [ignore, setIgnore] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [confirmed, setConfirmed] = useState<confirmItem[]>([]);
  const [shopCollectionAccepted, setShopCollectionAccepted] = useState<{ accepted: boolean; names: string[] } | null>(
    null
  );
  const [dispatchBlocked, setDispatchBlocked] = useState<{ references: string[]; reason?: string } | null>(null);
  const [businessDeliveries, setBusinessDeliveries] = useState<BusinessDelivery[]>([]);
  const { track } = useSmartSendAnalytics();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const canInstantPay = useMemo(() => {
    return (
      providerGroups.length > 1 &&
      !providerGroups.find((x) => !!x.ShipResponse) &&
      providerGroups.length ===
        providerGroups.filter((x) =>
          ['Invoiced', 'TopUp', 'SavedPayment', 'CourierAccount'].includes(x.SelectedPaymentMethod)
        ).length
    );
  }, [providerGroups]);

  const { useListener } = useEventEmitter<BookEventType>();
  useListener(
    Consts.Book,
    (x) => {
      setIds(x.Ids);
      setIgnore([]);
      setOpen(x.Open);
    },
    []
  );

  const logEvent = (event: string, clickType: ClickType, payload?: any, extra?: Record<string, unknown>) => {
    track({
      type: `${event}_${clickType}`.toLowerCase(),
      payload: {
        ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.Book]),
        action: payload,
        ...extra
      }
    });
  };

  const providers = useMemo(() => {
    return providerGroups.map((g) => g.Provider.Provider).filter(onlyUnique);
  }, [providerGroups]);

  const multiProvider = providers.length > 1;

  useEffect(() => {
    async function getData() {
      track({
        type: 'book_panel_loaded',
        payload: {
          ...GenerateBaseAnalyticsPayload([EventSections.SmartSend, EventSections.Book])
        }
      });

      // Get Bookable Items
      let items =
        ids?.length > 0
          ? GetBookableBasedOnIds(ids, SmartSendItemsCache)
          : GetBookableBasedOnSelection(props.filterKey, ctx.state, SmartSendItemsCache);

      // Override List
      if (ignore.length > 0) {
        items = items.filter((x) => !ignore.includes(x.Id));

        if (items.length == 0) {
          onClose();
          return;
        }
      }

      // Sorting
      items = items.sort((a, b) => {
        return a.State?.order > b.State?.order ? 1 : -1;
      });

      //  Update Collection Dates
      if (SettingsCache.settings.Features.includes('RefreshCollectionDates')) {
        await RefreshCollectionDates(items, SettingsCache.settings.Features.includes('ForceEarliestCollectionDate'));
      }

      const itemsByProvider = GetItemsByProvider(items, SettingsCache.settings.Limits.SmartSendOrderSize);
      setProviderGroups(itemsByProvider);

      // Get payment options
      const providerKeys: CachedPaymentOption[] = [];
      itemsByProvider.forEach((p) => {
        if (!providerKeys.find((x) => x.accountId == p.Provider.AccountId && x.provider === p.Provider.Provider)) {
          providerKeys.push({
            provider: p.Provider.Provider,
            accountId: p.Provider.AccountId,
            options: []
          });
        }
      });

      const paymentOptions = providerKeys.map((x) =>
        getAccountPaymentOptions(x.provider, x.accountId).then((r) => {
          return {
            ...x,
            options: r.success ? r.result.PaymentOptions : []
          };
        })
      );

      // Wait for all
      await Promise.all(paymentOptions).then((x) => {
        setPaymentOptions(x);
        setConfirmed([]);
        setLoading(false);
      });

      const shopDeliveryItems = items.filter((x) => isDeliveryToShopService(x));
      if (shopDeliveryItems.length > 0) {
        setShopCollectionAccepted({ accepted: false, names: shopDeliveryItems.map((x) => x.Recipient.Name) });
      }

      const getOrderReference = (x: SmartSendItemApiModel) =>
        x.MarketplaceOrderLines?.[0]?.MarketplaceOrderId ?? x.ReferenceSummary;

      const dispatchBlockedOrders = getDispatchBlockedOrders(items, getOrderReference);
      if (dispatchBlockedOrders.length > 0) {
        setDispatchBlocked({
          reason: getSharedDispatchBlockedReason(dispatchBlockedOrders),
          references: dispatchBlockedOrders.map((x) => x.reference)
        });
      }

      setBusinessDeliveries(getBusinessDeliveries(items, getOrderReference));
    }
    open && getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ignore]);

  const removeItems = (ids: string[]) => {
    setIgnore([...ignore, ...ids].filter(onlyUnique));
  };

  const onClose = () => {
    if (!confirmed.find((x) => !x.confirmed && x.count > 0)) {
      setLoading(true);
      setIds([]);
      setIgnore([]);
      setOpen(false);
      setShopCollectionAccepted(null);
      setDispatchBlocked(null);
      setBusinessDeliveries([]);
    }
  };

  const setState = (
    group: ProviderGroup,
    state: ProviderGroupState,
    shipResponse: ShipResponse,
    markAsDispatched: boolean
  ): ProviderGroup[] => {
    const nItems = [...providerGroups];
    nItems.forEach((x) => {
      if (group.Key === x.Key) {
        group.State = state;
        group.ShipResponse = shipResponse;
        group.MarkedAsDispatched = markAsDispatched;
      }
    });
    setProviderGroups(nItems);
    return nItems;
  };

  const markAsBookedAndAddLinks = (key: string, links: LinkApiModel[]) => {
    const nItems = [...providerGroups];
    for (const x of nItems) {
      if (x.Key === key) {
        if (links != null) {
          x.State = 'BOOKED';
          x.ShipResponse = { ...x.ShipResponse, Links: links, BookingStatus: 'Booked' };
        }
      }
    }
    setProviderGroups(nItems);
  };

  const onStagedItemNowBooked = async (g: ProviderGroup) => {
    // If a staged order has all the tracking records then we can get the links and show on the panel
    if (g.ShipResponse?.BookingStatus === 'Staged') {
      const links = await getShipmentActions(g.Provider.Provider, g.Provider.AccountId, g.ShipResponse.Reference).then(
        (x) => {
          if (x.success && x.result && x.result.Links) {
            return x.result.Links;
          }
        }
      );
      if (links != null) {
        markAsBookedAndAddLinks(g.Key, links);
        const gIds = g.Items.map((x) => x.Id);
        const updatedItems = await getItemsByIds(gIds);
        if (updatedItems.success) {
          ctx.dispatch(new MarkAsPaidItemsAction(updatedItems.result.Items));
        } else {
          ctx.dispatch(new MarkAsPaidItemsTempAction(gIds));
        }
      }
    }
  };

  const updatePaymentOptions = (key: string, option: PaymentOptionType, optionOption: PaymentOptionOptionApiModel) => {
    // Read the group before mutating so `from` is the option being switched away from.
    const group = providerGroups.find((x) => x.Key === key);
    const prePayBalance = paymentOptions
      .find((o) => o.accountId === group?.Provider.AccountId && o.provider === group?.Provider.Provider)
      ?.options.find((o) => o.Type === 'TopUp')?.Balance;

    logEvent('payment-method', 'selected', option, {
      from: group?.SelectedPaymentMethod,
      to: option,
      provider: group?.Provider.Provider,
      totalPrice: group?.TotalPrice,
      currencyCode: group?.CurrencyCode,
      prePayBalance
    });
    const nItems = [...providerGroups];
    nItems.forEach((x) => {
      if (x.Key === key) {
        x.SelectedPaymentMethod = option;
        x.SelectedPaymentOption = optionOption;
      }
    });
    setProviderGroups(nItems);
  };

  const updateDiscountCode = (key: string, discountCode: string) => {
    const nItems = [...providerGroups];
    nItems.forEach((x) => {
      if (x.Key === key) {
        x.Discount = {
          DiscountCode: discountCode,
          Loading: !!discountCode,
          Applied: false,
          TotalDiscount: 0,
          TotalPrice: 0,
          TotalPriceExVat: 0,
          TotalVat: 0
        };
      }
      if (discountCode) {
        getDiscountAmount(x);
      }
    });
    setProviderGroups(nItems);
  };

  const getDiscountAmount = async (group: ProviderGroup) => {
    const v = await verifyShip(
      group.Provider.Provider,
      group.Provider.AccountId,
      group.Items,
      group.Discount.DiscountCode
    );

    const nItems = [...providerGroups];
    nItems.forEach((x) => {
      if (x.Key === group.Key) {
        if (v.success) {
          x.Discount.Applied = true;
          x.Discount.TotalDiscount = v.result.TotalDiscount;
          x.Discount.TotalPrice = v.result.TotalPrice;
          x.Discount.TotalPriceExVat = v.result.TotalPriceExVat;
          x.Discount.TotalVat = v.result.TotalVat;
          x.Discount.Breakdown = v.result.Breakdown;
        }
        x.Discount.Loading = false;
      }
    });
    setProviderGroups(nItems);
  };

  const addToSafeList = (ids: string[]) => {
    ids.forEach((x) => {
      if (!ExtendedOrderCache.recentlyBookedSmartSendItems.includes(x)) {
        ExtendedOrderCache.recentlyBookedSmartSendItems.push(x);
      }
    });
  };

  const addTypeSuccess = (type: confirmType) => {
    const confirmedArray = [...confirmed];
    if (confirmed.find((x) => x.type === type)) {
      confirmedArray.forEach((c) => {
        if (c.type === type) {
          c.count++;
        }
      });
    } else {
      confirmedArray.push({ type: type, confirmed: false, count: 1 });
    }
    setConfirmed(confirmedArray);
  };

  const setTypeStatus = (type: confirmType, done: boolean) => {
    const confirmedArray = [...confirmed];
    confirmedArray.forEach((c) => {
      if (c.type === type) {
        c.confirmed = done;
      }
    });
    setConfirmed(confirmedArray);
  };

  const book = async (group: ProviderGroup) => {
    logEvent('booklabels', 'click');
    setState(group, 'BOOKING', null, false);

    const discountCode = CanUseDiscount(group.SelectedPaymentMethod) ? group.Discount?.DiscountCode : null;

    let errorMessage = '';

    try {
      const r = await ship(
        group.Provider.Provider,
        group.Provider.AccountId,
        group.Items,
        group.SelectedPaymentMethod,
        group.SelectedPaymentOption?.Id,
        discountCode
      );

      if (r.success) {
        if (['Failed', 'None'].includes(r.result.BookingStatus) || r.result?.Errors?.length > 0) {
          setState(group, 'ERROR', r.result, false);
          return;
        }
        if (r.result.BookingStatus === 'Booked') {
          addToSafeList(group.Items.map((x) => x.Id));
          addTypeSuccess('LABELS');
          setState(group, 'BOOKED', r.result, false);

          const gIds = group.Items.map((x) => x.Id);
          const updatedItems = await getItemsByIds(gIds);
          if (updatedItems.success) {
            ctx.dispatch(new MarkAsPaidItemsAction(updatedItems.result.Items));
          } else {
            ctx.dispatch(new MarkAsPaidItemsTempAction(gIds));
          }

          // Do analytics
          record(r.result, group);

          return;
        }
        if (r.result.BookingStatus === 'Staged') {
          addToSafeList(group.Items.map((x) => x.Id));

          // Auto Store Booking Link TODO
          r.result.Links.filter((x) => ['ORDER-PAYMENT_PAGE'].includes(x.Type)).forEach((l) => {
            dctx.dispatch({
              type: 'ADD',
              payload: {
                Href: l.Href,
                Label: `Payment Link (${r.result.Reference}) \n ${l.Label}`,
                Status: 'Processing',
                Type: l.Type,
                UID: uid()
              }
            });
          });

          ctx.dispatch(new MarkAsStagedItemsAction(group.Items.map((x) => x.Id)));
          setState(group, 'STAGED', r.result, false);
          return;
        }
      } else {
        errorMessage = r.error?.message;
      }
    } catch (error) {
      if (['TimeoutError', 'AbortError'].includes(error?.name)) {
        errorMessage = t('BOOKING_ERROR_MESSAGE', {
          defaultValue:
            'We were unable to create your order. Please try again and if the issue persists, try booking with fewer items.'
        });
      }
    }

    setState(
      group,
      'ERROR',
      {
        Errors: [
          {
            Error:
              errorMessage ||
              t('ERROR_CREATING_ORDER', { defaultValue: 'We were unable to create your order. Please try again.' }),
            Type: '',
            ItemId: ''
          }
        ]
      },
      false
    );
  };

  const bookAll = () => providerGroups.forEach((i) => book(i));

  const markAsDispatched = (group: ProviderGroup) => {
    setState(group, group.State, group.ShipResponse, true);
    ctx.dispatch(new MarkDispatchedItemAction(group.Items.map((x) => x.Id)));
  };

  const mapName = (g: ProviderGroup) => {
    if (g.Provider.Provider === SmartSendProvider) {
      switch (g.SelectedPaymentMethod) {
        case 'TopUp':
          return 'PrePay';
        case 'External':
          return 'Card';
        case 'SavedPayment':
          return 'Stored Card';
        default:
          return g.SelectedPaymentMethod;
      }
    }
    return g.SelectedPaymentMethod;
  };

  const confirmRc = confirmed?.length > 0 && (
    <Confirm
      confirmed={confirmed}
      onClick={setTypeStatus}
      labelSummary={
        providerGroups.filter((x) => x.ShipResponse?.BookingStatus === 'Booked').length > 1 && (
          <LabelSummary providers={providerGroups} />
        )
      }
    />
  );

  return (
    <P2GSidePanel
      open={open}
      maxWidth="md"
      onClose={onClose}
      anchor="right"
      title={t('BOOKING_SUMMARY_SIDEPANEL_TITLE', { defaultValue: 'Booking Summary' })}
      width={'570px'}
    >
      {loading ? (
        <Loading />
      ) : (
        <Tagged tag="panel-book">
          <KycStatusCheck providerGroups={providerGroups}>
            {shopCollectionAccepted?.accepted === false ? (
              <ShopCollectionWarning
                names={shopCollectionAccepted.names}
                accept={() => setShopCollectionAccepted({ ...shopCollectionAccepted, accepted: true })}
              />
            ) : (
              <Suspense>
                <P2GBox textAlign="left" pb={4} classNames="p2g-bookorders-panel">
                  {!props.isXs &&
                    SettingsCache.settings.Features.includes('AutoMarkAsDispatchedBookingPrompt') &&
                    providerGroups.every((x) => x.State === 'IDLE') && (
                      <Suspense>
                        <InstaSetting
                          area="BOOK"
                          features={[
                            {
                              feature: 'SmartSendSettings',
                              userCanDismiss: true
                            }
                          ]}
                          wrapper={(x) => (
                            <P2GBox mb={3}>
                              <AppSurface>{x}</AppSurface>
                            </P2GBox>
                          )}
                        />
                      </Suspense>
                    )}
                  {dispatchBlocked && (
                    <P2GBox mb={3}>
                      <DispatchBlockedWarning references={dispatchBlocked.references} reason={dispatchBlocked.reason} />
                    </P2GBox>
                  )}
                  {providerGroups.length > 1 && (
                    <P2GBox pb={2}>
                      <P2GText component="p">
                        <Trans
                          i18nKey="BOOKING_SUMMARY_SIDEPANEL_DESCRIPTION"
                          t={t}
                          values={{
                            parcelCount: SumNumber(providerGroups.map((x) => x.Items.length)),
                            paymentMethods: ToInlineCommaList(providerGroups.map(mapName).filter(onlyUnique))
                          }}
                          defaults="<strong>{{parcelCount}} parcel(s)</strong> are ready to send. <br /> 
                    These orders will be processed in batches using the following payment methods: {{paymentMethods}}."
                        />
                      </P2GText>
                    </P2GBox>
                  )}
                  {providers.map((p) => (
                    <P2GBox key={p} bordered={multiProvider} p={multiProvider ? 2 : 0} mb={2}>
                      <ProviderGroups
                        isXs={props.isXs}
                        removeItems={removeItems}
                        onBooked={onStagedItemNowBooked}
                        onClose={onClose}
                        providerGroups={providerGroups.filter((x) => x.Provider.Provider === p)}
                        getPaymentOptions={(x) =>
                          paymentOptions.find(
                            (o) => o.accountId == x.Provider.AccountId && o.provider === x.Provider.Provider
                          )?.options
                        }
                        setState={setState}
                        markAsDispatched={markAsDispatched}
                        book={book}
                        canInstantPay={canInstantPay}
                        updateDiscountCode={updateDiscountCode}
                        setProviderGroups={updatePaymentOptions}
                        confirmComponent={confirmRc}
                      />
                    </P2GBox>
                  ))}
                  {canInstantPay && (
                    <>
                      {confirmRc}
                      <BookAll
                        termsLinks={providerGroups.flatMap((x) => x.Links || [])}
                        isXs={props.isXs}
                        disabled={loadingManifests}
                        bookAll={bookAll}
                        groupCount={providerGroups.length}
                        states={providerGroups.map((x) => x.State)}
                        close={onClose}
                      />
                    </>
                  )}
                  {/* Below the book button on purpose - a seller with dozens of business orders would otherwise
                      have to scroll past every one of them to book. */}
                  {businessDeliveries.length > 0 && (
                    <P2GBox mt={3}>
                      <BusinessDeliveryNotice deliveries={businessDeliveries} />
                    </P2GBox>
                  )}
                </P2GBox>
              </Suspense>
            )}
          </KycStatusCheck>
        </Tagged>
      )}
    </P2GSidePanel>
  );
}
